import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { logAmplitudeEvent } from '../../../common/services/amplitudeService';
import { useLoyalty } from '../../LoyaltyContext';
import HeroStatusCard from '../HeroStatusCard/HeroStatusCard';
import TierRewards from '../TierRewards/TierRewards';
import PointsRules from '../PointsRules/PointsRules';
import PointsHistory from '../PointsHistory/PointsHistory';

const LoyaltyPage = () => {
    const { t } = useTranslation();
    const { loyalty: data, eligible, refresh } = useLoyalty();

    // Refresh on navigation to /loyalty — the route unmounts/remounts the page (App.js), so this
    // fires on every visit. On revisit the page renders instantly from cached data while
    // refreshing in the background instead of flashing blank.
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Fires once per mount when data is already cached, or once when eligibility resolves after
    // the refresh; `eligible` never toggles repeatedly mid-session.
    useEffect(() => {
        if (eligible) logAmplitudeEvent('Loyalty dashboard viewed');
    }, [eligible]);

    if (!data || !data.eligible) return null;
    return (
        <Box sx={{ maxWidth: 1260, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, pt: 6, pb: 8 }}>
            <Stack spacing={6}>
                <Stack spacing={3}>
                    <Box>
                        <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{ color: 'text.primary', fontSize: { xs: '2rem', md: '2.75rem' } }}
                            >
                                {t('loyalty.title')}
                            </Typography>
                            <Chip
                                label={t('loyalty.badgeNew')}
                                size="small"
                                sx={{
                                    height: 22,
                                    borderRadius: '999px',
                                    bgcolor: 'grey.100',
                                    color: 'text.secondary',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                }}
                            />
                        </Stack>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            {t('loyalty.subtitle')}
                        </Typography>
                    </Box>
                    <HeroStatusCard data={data} />
                </Stack>
                <TierRewards data={data} />
                <PointsRules />
                <PointsHistory history={data.history} qualifyingPoints={data.qualifyingPoints} />
            </Stack>
        </Box>
    );
};

export default LoyaltyPage;
